import React, { useState, useEffect, useMemo } from "react";
import FormPurchase from "./FormPurchase";
import {
  fetchPurchases,
  addPurchase,
  updatePurchase,
  deletePurchase,
  settlePurchase,
} from "../../actions/purchaseAction";
import { fetchMaterials } from "../../actions/materialAction";
import { fetchSuppliers } from "../../actions/supplierAction";
import { fetchUnits } from "../../actions/unitAction";
import { connect } from "react-redux";
import { AlertConfirmModal } from "../../components/Modals/AlertConfirmModal";
import AkasSelect from "../../components/AkasSelect";
import { sortHelper } from "../../utils/helpers/sortHelper";
import { useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";

import { formatIDR } from "../../utils/helpers/formatIDR";
import { format, startOfMonth } from "date-fns";
import { useNavigate } from "react-router-dom";
import DetailPurchase from "./DetailPurchase";
import { BASE_URL_STORAGE } from "../../helpers/config";
import PaymentSettlementModal from "../../components/Modals/PaymentSettlementModal";
import { hasAnyPermission } from "../../utils/helpers/permissions";

const today = format(new Date(), "yyyy-MM-dd");
const monthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");

const toPurchaseFormState = (purchase = {}) => ({
  id: purchase?.id || "",
  supplier_id: purchase?.supplier_id || "",
  supplier_name: purchase?.supplier_name || "",
  purchase_date: purchase?.purchase_date || today,
  payment_method: purchase?.payment_method || "cash",
  total: Number(purchase?.total || 0),
  pay: Number(purchase?.pay || 0),
  due: Number(purchase?.due || 0),
  payment_status: purchase?.payment_status || "unpaid",
  details: purchase?.details || [],
  note: purchase?.note || "",
  cancellation_reason: purchase?.cancellation_reason || "",
  cancelled_at: purchase?.cancelled_at,
  cancelled_by: purchase?.cancelled_by,
  created_at: purchase?.created_at,
  updated_at: purchase?.updated_at,
});

const purchaseStatus = {
  paid: { label: "Lunas", className: "bg-success-lt" },
  unpaid: { label: "Belum Lunas", className: "bg-danger-lt" },
  cancelled: { label: "Dibatalkan", className: "bg-secondary-lt text-secondary" },
};

const getPurchaseStatus = (status) => purchaseStatus[status] || { label: status || "-", className: "bg-secondary-lt" };

const SummaryCard = ({ title, value, subtitle, color = "primary", icon }) => (
  <div className="col-4 akas-summary-column--three-up">
    <div className="card h-100 akas-summary-card akas-summary-card--three-up">
      <div className="card-body">
        <div className="d-flex align-items-center gap-3">
          <span className={`avatar bg-${color}-lt text-${color} rounded-3 d-none d-lg-block`}>{icon}</span>
          <div className="akas-summary-card-content w-100">
            <div className="text-secondary small fw-bold text-uppercase text-truncate" title={title}>
              {title}
            </div>
            <div className="h1 mb-0 fw-bold d-none d-lg-block" title={String(value)}>
              {value}
            </div>
            <div className="h4 mb-0 fw-bold d-block d-lg-none text-truncate" title={String(value)}>
              {value}
            </div>
            {subtitle ? <div className="text-secondary small mt-1 d-none d-lg-block">{subtitle}</div> : null}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PurchasePage = (props) => {
  const {
    dataPurchases,
    purchases,
    isLoading,
    fetchPurchases,
    addPurchase,
    updatePurchase,
    deletePurchase,
    settlePurchase,
    fetchMaterials,
    materials,
    suppliers,
    fetchSuppliers,
    fetchUnits,
    units,
    purchaseError,
    userProfile,
  } = props;
  const canProcess = hasAnyPermission(userProfile, ["process-purchases"]);

  const _navigate = useNavigate();
  const [perPage, setPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const initialState = {
    id: "",
    supplier_id: "",
    supplier_name: "",
    purchase_date: format(new Date(), "yyyy-MM-dd"),
    payment_method: "cash",
    total: "",
    pay: "",
    due: "",
    payment_status: "",
    note: "",
    details: [
      {
        id: "",
        purchase_id: "",
        is_consumable: false,
        item_type: "material",
        material_id: "",
        material_name: "",
        unit: "pcs",
        weight: "",
        qty: 1,
        unit_cost: "",
        sub_total: 0,
      },
    ],
  };
  const [formState, setFormState] = useState(initialState);

  const [isShowModal, setIsShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: monthStart, endDate: today });
  const [searchParams, setSearchParams] = useSearchParams();
  const searchFromUrl = searchParams.get("search") || "";

  const [filterText, setFilterText] = useState(searchFromUrl);

  const { sortConfig, handleSort, getSortedData } = sortHelper.useSort();
  const openModal = () => {
    setFormState(initialState);
    setIsShowModal(true);
  };
  const closeModal = () => {
    setFormState(initialState);
    setIsShowModal(false);
    setIsEdit(false);
  };
  const handleDelete = async (id, reason) => {
    const success = await deletePurchase(id, reason, { refreshPurchases: false });
    if (success) {
      await fetchPurchases(perPage, currentPage, searchFromUrl, statusFilter, fetchFilter);
    }
    return success;
  };
  const handleAdd = async (data = formState) => {
    const success = await addPurchase(data);
    if (success) {
      setCurrentPage(1);
      fetchPurchases(perPage, 1, searchFromUrl, statusFilter, fetchFilter);
    }
    return success;
  };
  const handleUpdate = async (data = formState) => {
    const success = await updatePurchase(data?.id || formState?.id, data);
    if (success) {
      fetchPurchases(perPage, currentPage, searchFromUrl, statusFilter, fetchFilter);
    }
    return success;
  };
  const handleSettle = async (id, payload) => {
    if (!canProcess) return false;
    const success = await settlePurchase(id, payload, { refreshPurchases: false });
    if (success) {
      fetchPurchases(perPage, currentPage, searchFromUrl, statusFilter, fetchFilter);
    }
  };
  const fetchFilter = useMemo(
    () => ({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    }),
    [dateRange.endDate, dateRange.startDate],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilterText(value);

    if (value) {
      setSearchParams({ search: value });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    setFilterText(searchFromUrl);
  }, [searchFromUrl]);

  const debouncedFetch = useMemo(
    () =>
      debounce((page, limit, search) => {
        fetchPurchases(limit, page, search, statusFilter, fetchFilter);
      }, 500),
    [fetchPurchases, statusFilter, fetchFilter],
  );
  useEffect(() => {
    debouncedFetch(currentPage, perPage, searchFromUrl);

    return () => {
      debouncedFetch.cancel();
    };
  }, [currentPage, perPage, searchFromUrl, statusFilter, dateRange.startDate, dateRange.endDate, debouncedFetch]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFromUrl, statusFilter, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    fetchMaterials();
    fetchSuppliers();
    fetchUnits(500);
  }, [fetchMaterials, fetchSuppliers, fetchUnits]);

  const purchaseSummary = useMemo(() => {
    const summary = dataPurchases?.summary || {};
    const itemQty = purchases.reduce(
      (total, purchase) =>
        total + (purchase?.details || []).reduce((detailTotal, detail) => detailTotal + Number(detail?.qty || 0), 0),
      0,
    );
    const paidPurchase = purchases
      .filter((purchase) => purchase?.payment_status === "paid")
      .reduce((total, purchase) => total + Number(purchase?.total || 0), 0);

    return {
      transactionCount:
        summary.total_transactions ?? summary.transaction_count ?? dataPurchases?.total ?? purchases.length,
      itemQty: summary.total_items ?? summary.total_qty ?? itemQty,
      paidPurchase: summary.paid_purchase ?? summary.paid_total ?? paidPurchase,
    };
  }, [dataPurchases?.summary, dataPurchases?.total, purchases]);

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h1 className="page-title fs-1 text-capitalize">Pembelian Material</h1>
              <div className="page-pretitle">Pembelian bahan, supplier, dan persediaan</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button
                  type="button"
                  className="btn btn-primary btn-5 d-none d-sm-inline-block"
                  data-bs-toggle="modal"
                  data-bs-target="#modal-form"
                  onClick={() => {
                    setFormState(initialState);
                    setIsEdit(false);
                    openModal();
                  }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-2">
                    <path d="M12 5l0 14" />
                    <path d="M5 12l14 0" />
                  </svg>
                  Tambah Pembelian
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-6 d-sm-none btn-icon"
                  data-bs-toggle="modal"
                  data-bs-target="#modal-form"
                  onClick={openModal}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-2">
                    <path d="M12 5l0 14" />
                    <path d="M5 12l14 0" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          {purchaseError ? (
            <div className="alert alert-danger d-flex align-items-start gap-2" role="alert">
              <strong>Daftar pembelian belum dapat dimuat.</strong>
              <span>{purchaseError?.message || purchaseError?.data?.message || String(purchaseError)}</span>
              <button
                type="button"
                className="btn btn-sm btn-danger ms-auto"
                onClick={() => fetchPurchases(perPage, currentPage, searchFromUrl, statusFilter, fetchFilter)}>
                Coba lagi
              </button>
            </div>
          ) : null}
          <div className="row row-deck row-cards">
            <SummaryCard
              title="Transaksi"
              value={Number(purchaseSummary.transactionCount || 0).toLocaleString("id-ID")}
              subtitle="Dalam rentang tanggal"
              color="primary"
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M5 3h14v18l-3-2l-2 2l-2-2l-2 2l-2-2l-3 2z" />
                  <path d="M9 8h6M9 12h6" />
                </svg>
              }
            />
            <SummaryCard
              title="Item"
              value={Number(purchaseSummary.itemQty || 0).toLocaleString("id-ID")}
              subtitle="Total qty pembelian"
              color="warning"
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M4 7l8-4l8 4v10l-8 4l-8-4z" />
                  <path d="M4 7l8 4l8-4M12 11v10" />
                </svg>
              }
            />
            <SummaryCard
              title="Pembelian Lunas"
              value={formatIDR(purchaseSummary.paidPurchase || 0)}
              subtitle="Transaksi berstatus lunas"
              color="success"
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M3 6h18v12H3z" />
                  <path d="M7 10h.01M17 14h.01" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
              }
            />
            <div className="col-12">
              <div className="card">
                <div className="card-table">
                  <div className="card-header ">
                    <div className="row w-full align-items-center">
                      <div className="col-lg-3 col-12">
                        <h3 className="card-title mb-0">Daftar Pembelian</h3>
                        <p className="text-secondary m-0">Pantau tagihan dan penerimaan material</p>
                      </div>
                      <div className="col-lg-2 col-md-4 col-4 my-lg-0 my-2">
                        <AkasSelect
                          className="form-select"
                          value={statusFilter}
                          onChange={(event) => setStatusFilter(event.target.value)}>
                          <option value="">Semua Status</option>
                          <option value="paid">Lunas</option>
                          <option value="unpaid">Belum Lunas</option>
                          <option value="cancelled">Dibatalkan</option>
                        </AkasSelect>
                      </div>
                      <div className="col-lg-2 col-md-4 col-4 my-lg-0 my-2">
                        <input
                          type="date"
                          className="form-control"
                          value={dateRange.startDate}
                          onChange={(event) => setDateRange((prev) => ({ ...prev, startDate: event.target.value }))}
                        />
                      </div>
                      <div className="col-lg-2 col-md-4 col-4 my-lg-0 my-2">
                        <input
                          type="date"
                          className="form-control"
                          value={dateRange.endDate}
                          onChange={(event) => setDateRange((prev) => ({ ...prev, endDate: event.target.value }))}
                        />
                      </div>
                      <div className="col-lg-3 col-md-12 col-12 my-lg-0 my-2">
                        <div className="input-group input-group-flat w-auto">
                          {isLoading ? (
                            <span className="input-group-text">
                              <div
                                className="spinner-border spinner-border-sm text-primary text-center"
                                role="status"></div>
                            </span>
                          ) : (
                            <span className="input-group-text">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="icon icon-1">
                                <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
                                <path d="M21 21l-6 -6" />
                              </svg>
                            </span>
                          )}

                          <input
                            id="advanced-table-search"
                            type="text"
                            className="form-control"
                            autoComplete="off"
                            onChange={handleSearchChange}
                            placeholder="Cari..."
                            value={filterText}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div id="advanced-table">
                    <div className="table-responsive">
                      <table className="table table-vcenter table-selectable">
                        <thead className="table-header">
                          <tr>
                            <th className="sort-name py-3 w-1">No.</th>

                            <sortHelper.SortableHeader
                              label="Supplier"
                              sortKey="supplier_name"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />

                            <sortHelper.SortableHeader
                              label="Total"
                              sortKey="total"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />

                            <th className="sort-status fw-bold">Bayar</th>
                            <th className="sort-status fw-bold">Hutang</th>
                            <th className="sort-status fw-bold">Tanggal Pembelian</th>
                            <th className="sort-status fw-bold">Status</th>
                            <th className="sort-status fw-bold">Material</th>
                            <th
                              className="text-start"
                              style={{
                                width: "25%",
                              }}></th>
                          </tr>
                        </thead>

                        <tbody className="table-tbody">
                          {isLoading
                            ? [...Array(purchases?.length || 5)]?.map((_, index) => (
                                <tr
                                  key={`loading-${index}`}
                                  style={{
                                    cursor: "loader",
                                  }}>
                                  <td className="sort-name py-4">{(currentPage - 1) * perPage + index + 1}.</td>
                                  {[...Array(7)].map((_, i) => (
                                    <td key={i}>
                                      <div className="placeholder placeholder-lg w-75"></div>
                                    </td>
                                  ))}
                                  <td className="justify-content-start text-start flex-nowrap">
                                    <div
                                      className="placeholder placeholder-lg me-2 "
                                      style={{
                                        width: "40%",
                                      }}></div>
                                    <div
                                      className="placeholder placeholder-lg "
                                      style={{
                                        width: "40%",
                                      }}></div>
                                  </td>
                                </tr>
                              ))
                            : getSortedData(purchases)?.map((Purchase, index) => (
                                <tr
                                  key={Purchase.id}
                                  className={Purchase?.payment_status === "cancelled" ? "opacity-75" : ""}>
                                  <td className="sort-name">{(currentPage - 1) * perPage + index + 1}.</td>

                                  <td data-label="Nama">{Purchase.supplier_name}</td>
                                  <td className="sort-id">
                                    <span className="badge fw-bold">{formatIDR(Purchase?.total)}</span>
                                  </td>
                                  <td className="sort-city">
                                    <span className="badge fw-bold text-primary">{formatIDR(Purchase?.pay)}</span>
                                  </td>
                                  <td className="sort-city">
                                    <span className="badge fw-bold text-danger">{formatIDR(Purchase?.due)}</span>
                                  </td>

                                  <td className="sort-status text-center">
                                    {format(new Date(Purchase?.purchase_date), "dd MMM yyyy")}
                                  </td>
                                  <td className="sort-status fw-bold text-uppercase text-center">
                                    <span className={`badge ${getPurchaseStatus(Purchase?.payment_status).className}`}>
                                      {getPurchaseStatus(Purchase?.payment_status).label}
                                    </span>
                                  </td>
                                  <td className="sort-status fw-bold  text-primary text-start">
                                    {Purchase?.details?.map((item, index) => {
                                      const dataImageUrl = item.image ? BASE_URL_STORAGE + item.image : null;

                                      const name = item?.material_name?.trim() || "";
                                      const shortName = name.length > 10 ? name.substring(0, 30) + "..." : name;

                                      return (
                                        <span className="tag text-truncate fs-6 m-1" key={index} title={name}>
                                          <span
                                            className="avatar avatar-xxs tag-avatar fs-6"
                                            style={{
                                              backgroundImage: dataImageUrl ? `url(${dataImageUrl})` : "none",
                                            }}>
                                            {!dataImageUrl && name.charAt(0)}
                                          </span>

                                          {shortName}
                                        </span>
                                      );
                                    })}
                                  </td>

                                  <td>
                                    <div className="btn-list flex-nowrap justify-content-center">
                                      <button
                                        type="button"
                                        className="btn btn-1 bg-secondary-lt"
                                        data-bs-toggle="modal"
                                        data-bs-target="#modal-form-detail"
                                        aria-label={`Lihat detail pembelian ${Purchase?.supplier_name || Purchase?.id}`}
                                        onClick={() => {
                                          setIsEdit(false);
                                          setFormState(toPurchaseFormState(Purchase));
                                          setIsShowModal(true);
                                        }}>
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="icon icon-tabler icons-tabler-outline icon-tabler-list-details">
                                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                          <path d="M13 5h8" />
                                          <path d="M13 9h5" />
                                          <path d="M13 15h8" />
                                          <path d="M13 19h5" />
                                          <path d="M3 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4" />
                                          <path d="M3 15a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -4" />
                                        </svg>
                                        Detail{" "}
                                      </button>

                                      {Purchase?.payment_status !== "cancelled" ? (
                                        <button
                                          type="button"
                                          className="btn btn-1 bg-primary-lt"
                                          data-bs-toggle="modal"
                                          data-bs-target="#modal-form"
                                          aria-label={`Ubah pembelian ${Purchase?.supplier_name || Purchase?.id}`}
                                          onClick={() => {
                                            setIsEdit(true);
                                            setFormState(toPurchaseFormState(Purchase));
                                            setIsShowModal(true);
                                          }}>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="icon">
                                            <path d="M12 20h9" />
                                            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1l1-4z" />
                                          </svg>
                                          Ubah
                                        </button>
                                      ) : null}

                                      {canProcess && Purchase?.payment_status === "unpaid" ? (
                                        <button
                                          type="button"
                                          className="btn btn-1 bg-primary-lt"
                                          data-bs-toggle="modal"
                                          data-bs-target="#modal-settle-purchase"
                                          aria-label="Proses pelunasan"
                                          onClick={() => {
                                            setFormState(toPurchaseFormState(Purchase));
                                          }}>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="icon">
                                            <path d="M12 3v18" />
                                            <path d="M17 8h-7.5a2.5 2.5 0 0 0 0 5h5a2.5 2.5 0 0 1 0 5h-8.5" />
                                          </svg>
                                          Lunasi
                                        </button>
                                      ) : null}

                                      {Purchase?.payment_status !== "cancelled" ? (
                                        <button
                                          type="button"
                                          className="btn btn-1 bg-danger-lt"
                                          data-bs-toggle="modal"
                                          data-bs-target="#modal-alert"
                                          aria-label={`Batalkan pembelian ${Purchase?.supplier_name || Purchase?.id}`}
                                          onClick={() => {
                                            setFormState(toPurchaseFormState(Purchase));
                                          }}>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="icon">
                                            <path d="M18 6 6 18M6 6l12 12" />
                                          </svg>
                                          Batalkan
                                        </button>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="card-footer d-flex align-items-center">
                      <div className="dropdown">
                        <button className="btn dropdown-toggle" data-bs-toggle="dropdown">
                          <span id="page-count" className="me-1">
                            {perPage}
                          </span>
                          <span>records</span>
                        </button>
                        <div className="dropdown-menu">
                          {[10, 20, 50, 100]?.map((value) => (
                            <button key={value} className="dropdown-item" onClick={() => handlePerPageChange(value)}>
                              {value} records
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="d-flex ">
                        <p className="ms-2 my-auto text-secondary">
                          {dataPurchases?.per_page} dari <span>{dataPurchases?.total}</span> <span>data</span>
                        </p>
                      </div>

                      <ul className="pagination m-0 ms-auto">
                        {/* PREV BUTTON */}
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>
                            <svg width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                              <path d="M15 6l-6 6l6 6" />
                            </svg>
                          </button>
                        </li>

                        {/* PAGE NUMBERS */}
                        {dataPurchases?.links?.slice(1, -1)?.map((item, index) => {
                          const pageNumber = item.url ? new URL(item.url).searchParams.get("page") : null;

                          return (
                            <li key={index} className={`page-item ${item?.active ? "active" : ""}`}>
                              <button
                                className="page-link"
                                disabled={!pageNumber}
                                onClick={() => pageNumber && setCurrentPage(Number(pageNumber))}>
                                {item.label}
                              </button>
                            </li>
                          );
                        })}

                        {/* NEXT BUTTON */}
                        <li className={`page-item ${currentPage === dataPurchases?.last_page ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(parseInt(dataPurchases?.current_page) + 1)}>
                            <svg width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                              <path d="M9 6l6 6l-6 6" />
                            </svg>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FormPurchase
        paymentReadOnly={isEdit && !canProcess}
        isShowModal={isShowModal}
        isEdit={isEdit}
        closeModal={closeModal}
        formState={formState}
        setFormState={setFormState}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
        materials={materials}
        initialState={initialState}
        suppliers={suppliers}
        units={units}
      />

      <DetailPurchase
        isShowModal={isShowModal}
        isEdit={isEdit}
        closeModal={closeModal}
        formState={formState}
        setFormState={setFormState}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
        materials={materials}
        initialState={initialState}
        suppliers={suppliers}
        units={units}
      />

      <PaymentSettlementModal
        idModal="settle-purchase"
        title="Pelunasan Pembelian"
        transaction={formState}
        nameLabel="Supplier"
        nameValue={formState?.supplier_name}
        loading={isLoading}
        onConfirm={handleSettle}
      />

      <AlertConfirmModal
        type="error"
        title="Batalkan pembelian?"
        message={`Pembelian ${
          formState?.details
            ?.map((item) => item.material_name)
            .filter(Boolean)
            .join(", ") || `#${formState?.id}`
        } pada tanggal ${formState?.purchase_date}. Stok material terkait akan dikembalikan. Apakah Anda yakin?`}
        confirmText="Batalkan"
        cancelText="Batal"
        requireReason
        reasonLabel="Alasan pembatalan"
        reasonPlaceholder="Contoh: invoice supplier salah, barang dikembalikan, atau transaksi ganda..."
        reasonHint="Tersimpan sebagai audit dan ikut dicatat pada reversal stok material."
        waitForConfirm
        idModal="alert"
        idData={formState?.id}
        onConfirm={(id, reason) => {
          return handleDelete(id, reason);
        }}
        onCancel={() => {
          // closeModalDelete();
        }}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  userProfile: state?.auth?.login?.profile,
  dataPurchases: state?.purchases?.datas,
  purchases: state?.purchases?.datas?.data || [],
  materials: (state?.materials?.datas?.data || []).filter((material) => !material.is_virtual_formula),
  suppliers: state?.suppliers?.datas?.data || [],
  units: state?.units?.datas?.data || [],
  isLoading: state?.purchases?.loading,
  purchaseError: state?.purchases?.error,
});

export default connect(mapStateToProps, {
  fetchPurchases,
  addPurchase,
  updatePurchase,
  deletePurchase,
  settlePurchase,
  fetchMaterials,
  fetchSuppliers,
  fetchUnits,
})(PurchasePage);
